import { useEffect, useState } from "react";
import {
  PageHeader,
  Card,
  CardHeader,
  FormField,
  TextInput,
  Select,
  Button,
  InlineAlert,
  Spinner,
} from "@constructorfleet/ui/design-system";

// Default canonical color list with emoji swatches for accessibility
const DEFAULT_COLORS: { colorName: string; emoji: string }[] = [
  { colorName: "Red", emoji: "🟥" },
  { colorName: "Orange", emoji: "🟧" },
  { colorName: "Yellow", emoji: "🟨" },
  { colorName: "Green", emoji: "🟩" },
  { colorName: "Blue", emoji: "🟦" },
  { colorName: "Purple", emoji: "🟪" },
  { colorName: "Brown", emoji: "🟫" },
  { colorName: "Black", emoji: "⬛" },
  { colorName: "White", emoji: "⬜" },
  { colorName: "Pink", emoji: "🌸" },
  { colorName: "Gray", emoji: "🔘" },
];

export function UaSignup() {
  const [states, setStates] = useState<{ code: string; name: string }[]>([]);
  const [jurisdictions, setJurisdictions] = useState<
    {
      id: number;
      name: string;
      callInLines: {
        id: number;
        phoneNumber: string;
        isColorCode: boolean;
        colors: { colorName: string }[];
      }[];
    }[]
  >([]);
  const [selectedJurisdictionId, setSelectedJurisdictionId] = useState<string>("");
  const [selectedCallInLineId, setSelectedCallInLineId] = useState<string>("");

  const initialLoading = typeof window === "undefined" ? false : true;
  const [loading, setLoading] = useState(initialLoading);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Jurisdictions loading state (per-state request)
  const [jurisdictionsLoading, setJurisdictionsLoading] = useState(false);
  const [jurisdictionsLoadError, setJurisdictionsLoadError] = useState<string | null>(null);

  const [selectedState, setSelectedState] = useState("");
  const [participantId, setParticipantId] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const res = await fetch("/api/website/ua/states", {
          headers: { Accept: "application/json" },
        });
        if (!active) return;
        if (!res.ok) {
          setLoadError("Unable to load supported states");
          setLoading(false);
          return;
        }
        const data = (await res.json()) as { ok?: boolean; states?: unknown };
        if (Array.isArray(data?.states)) {
          setStates(data.states as { code: string; name: string }[]);
        }
      } catch {
        if (!active) return;
        setLoadError("Unable to load supported states");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedState) {
      setJurisdictions([]);
      setSelectedJurisdictionId("");
      setSelectedCallInLineId("");
      setJurisdictionsLoadError(null);
      setJurisdictionsLoading(false);
      return;
    }
    let active = true;
    setJurisdictionsLoading(true);
    setJurisdictionsLoadError(null);
    void (async () => {
      try {
        const res = await fetch(
          `/api/website/co-ua/jurisdictions?stateCode=${encodeURIComponent(selectedState)}`
        );
        if (!active) return;
        if (!res.ok) {
          setJurisdictions([]);
          setJurisdictionsLoadError("Unable to load jurisdictions for selected state");
          return;
        }
        const data = (await res.json()) as { ok?: boolean; jurisdictions?: unknown };
        if (Array.isArray(data?.jurisdictions)) {
          setJurisdictions(data.jurisdictions as any[]);
        } else {
          setJurisdictions([]);
        }
      } catch {
        if (!active) return;
        setJurisdictions([]);
        setJurisdictionsLoadError("Unable to load jurisdictions for selected state");
      } finally {
        if (active) {
          setSelectedJurisdictionId("");
          setSelectedCallInLineId("");
          setJurisdictionsLoading(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [selectedState]);

  async function handleSubmit(e?: Event) {
    if (
      e &&
      typeof (e as unknown as { preventDefault?: () => void }).preventDefault === "function"
    ) {
      (e as unknown as { preventDefault: () => void }).preventDefault();
    }

    setFormError(null);
    setSubmitError(null);
    setSubmitted(false);

    if (!selectedState) {
      setFormError("State is required");
      return;
    }
    if (!selectedJurisdictionId) {
      setFormError("Jurisdiction is required");
      return;
    }
    if (!selectedCallInLineId) {
      setFormError("Phone number is required");
      return;
    }
    if (!participantId.trim()) {
      setFormError("Participant ID / color is required");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/website/co-ua/enroll", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stateCode: selectedState,
          participantId: participantId.trim(),
          jurisdictionId: selectedJurisdictionId,
          programId: null,
        }),
      });
      if (!res.ok) {
        setSubmitError("Something went wrong. Please try again.");
        setSubmitted(false);
        return;
      }
      const payload = (await res.json()) as { ok?: boolean; error?: string };
      if (!payload.ok) {
        setSubmitError("Something went wrong. Please try again.");
        setSubmitted(false);
        return;
      }
      setSubmitted(true);
    } catch {
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page ds-stack">
      <PageHeader
        title="UA Notifications"
        sub="Get set up with daily call-in notifications for your jurisdiction."
      />

      <Card>
        <CardHeader
          title="Your information"
          meta="Select your state and provide your details below."
        />
        <div className="card-body ds-stack">
          {/* Keep minimal static markers available for server-side rendering tests */}
          <span aria-hidden style={{ display: "none" }}>
            participant ID
          </span>
          <span aria-hidden style={{ display: "none" }}>
            assigned color
          </span>

          {loading ? (
            <div aria-label="loading-programs">
              <Spinner />
            </div>
          ) : loadError ? (
            <InlineAlert tone="danger">{loadError}</InlineAlert>
          ) : (
            <form
              onSubmit={(ev) => {
                ev.preventDefault();
                void handleSubmit(ev as unknown as Event);
              }}
            >
              {formError ? <InlineAlert tone="danger">{formError}</InlineAlert> : null}
              {submitError ? <InlineAlert tone="danger">{submitError}</InlineAlert> : null}
              {submitted ? (
                <InlineAlert tone="success">
                  You're all set — we'll start checking your line.
                </InlineAlert>
              ) : null}

              <FormField label="State" htmlFor="ua-state" required>
                <Select
                  id="ua-state"
                  value={selectedState}
                  onChange={(e) => {
                    setSelectedState((e.target as HTMLSelectElement).value);
                    setSelectedJurisdictionId("");
                  }}
                >
                  <option value="">Select a state</option>
                  {states.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </FormField>

              {/* Jurisdiction: hidden until a state is selected and jurisdictions are loaded */}
              {selectedState ? (
                jurisdictionsLoading ? (
                  <div aria-label="loading-jurisdictions">
                    <Spinner /> Loading jurisdictions...
                  </div>
                ) : jurisdictionsLoadError ? (
                  <InlineAlert tone="danger">{jurisdictionsLoadError}</InlineAlert>
                ) : jurisdictions.length > 0 ? (
                  <FormField label="Jurisdiction" htmlFor="ua-jurisdiction" required>
                    <Select
                      id="ua-jurisdiction"
                      value={selectedJurisdictionId}
                      onChange={(e) => {
                        setSelectedJurisdictionId((e.target as HTMLSelectElement).value);
                        setSelectedCallInLineId("");
                      }}
                    >
                      <option value="">Select a jurisdiction</option>
                      {jurisdictions.map((j) => (
                        <option key={j.id} value={String(j.id)}>
                          {j.name}
                        </option>
                      ))}
                    </Select>
                  </FormField>
                ) : (
                  <InlineAlert tone="neutral">
                    No jurisdictions found for selected state.
                  </InlineAlert>
                )
              ) : null}

              {/* Phone number: only shown when a jurisdiction with call-in lines is selected */}
              {selectedJurisdictionId
                ? (() => {
                    const sel = jurisdictions.find((j) => String(j.id) === selectedJurisdictionId);
                    if (!sel)
                      return (
                        <InlineAlert tone="neutral">Selected jurisdiction not found.</InlineAlert>
                      );
                    if (!sel.callInLines || sel.callInLines.length === 0)
                      return (
                        <InlineAlert tone="neutral">
                          No phone numbers found for selected jurisdiction.
                        </InlineAlert>
                      );

                    return (
                      <FormField label="Phone number" htmlFor="ua-callin" required>
                        <Select
                          id="ua-callin"
                          value={selectedCallInLineId}
                          onChange={(e) =>
                            setSelectedCallInLineId((e.target as HTMLSelectElement).value)
                          }
                        >
                          <option value="">Select a phone number</option>
                          {sel.callInLines.map((line) => (
                            <option key={line.id} value={String(line.id)}>
                              {line.phoneNumber}
                            </option>
                          ))}
                        </Select>
                      </FormField>
                    );
                  })()
                : null}

              {/* Participant ID or Color Code depending on selected call-in line (only shown after phone is selected) */}
              {selectedCallInLineId
                ? (() => {
                    const selJur = jurisdictions.find(
                      (j) => String(j.id) === selectedJurisdictionId
                    );
                    const selLine = selJur?.callInLines.find(
                      (l) => String(l.id) === selectedCallInLineId
                    );
                    if (!selLine)
                      return (
                        <InlineAlert tone="neutral">Selected phone line not found.</InlineAlert>
                      );

                    if (selLine.isColorCode) {
                      // Determine colors to show: prefer the stored list, otherwise fall back to the canonical list
                      const colorsToShow =
                        selLine.colors && selLine.colors.length > 0
                          ? selLine.colors.map((c: any) => ({
                              colorName: c.colorName,
                              emoji:
                                DEFAULT_COLORS.find(
                                  (d) =>
                                    d.colorName.toLowerCase() === String(c.colorName).toLowerCase()
                                )?.emoji ?? "🔘",
                            }))
                          : DEFAULT_COLORS;

                      return (
                        <FormField label="Assigned color" htmlFor="ua-color" required>
                          <Select
                            id="ua-color"
                            value={participantId}
                            onChange={(e) =>
                              setParticipantId((e.target as HTMLSelectElement).value)
                            }
                          >
                            <option value="">Select your color</option>
                            {colorsToShow.map((c) => (
                              <option key={c.colorName} value={c.colorName}>
                                {`${c.emoji} ${c.colorName}`}
                              </option>
                            ))}
                          </Select>
                        </FormField>
                      );
                    }

                    return (
                      <FormField label="Participant ID" htmlFor="ua-participant-id" required>
                        <TextInput
                          id="ua-participant-id"
                          value={participantId}
                          onChange={(e) => setParticipantId((e.target as HTMLInputElement).value)}
                          placeholder="e.g. ABC123"
                        />
                      </FormField>
                    );
                  })()
                : null}

              <div
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  alignItems: "center",
                  marginTop: "0.75rem",
                }}
              >
                <Button type="submit" disabled={submitting || submitted}>
                  {submitting ? "Setting up…" : "Start my notifications"}
                </Button>
                {submitting ? <Spinner size="sm" /> : null}
              </div>
            </form>
          )}
        </div>
      </Card>
    </div>
  );
}
